/* ==========================================================================
   Lockett Labs header, 2026
   Markup: sections/static-header.liquid, snippets/llh-panel.liquid
   Styles: assets/llh-header.css

   One file for everything the header does:
     1. sticky that tucks away on the way down and returns on the way up,
        publishing --header-height for the sticky filter sidebars
     2. the rolling store promises in the utility strip
     3. search: rolling placeholder, predictive results, recent searches
     4. the game rail: hover or tap panels, the sliding ink, the game features,
        newest arrivals fetched on first open
     5. the drawer for tablets and phones, built from the panels
     6. basket and watchlist counts

   Empire's own header module is not registered for this section, so nothing
   else binds to this markup. In the theme editor the section is re-rendered on
   every settings change; the previous instance tears down (every listener it
   put on window or document goes with one AbortController) and a new one boots
   on the fresh markup. Product and collection names are merchant data,
   so every string that reaches the page is set with textContent, never HTML,
   except the newest-arrivals block, which is this theme's own escaped section.
   ========================================================================== */
(function () {
  'use strict';

function boot() {
  var root = document.querySelector('[data-llh]');
  if (!root || root.__llhLive) return;
  if (window.__llhTeardown) window.__llhTeardown();
  root.__llhLive = true;

  var ac = window.AbortController ? new AbortController() : null;
  var timers = [];
  var cleanups = [];
  var on = function (target, type, fn, opts) {
    var o = { signal: ac ? ac.signal : undefined };
    if (opts) for (var k in opts) o[k] = opts[k];
    target.addEventListener(type, fn, o);
  };
  var every = function (fn, ms) { var id = setInterval(fn, ms); timers.push(id); return id; };
  window.__llhTeardown = function () {
    if (ac) ac.abort();
    timers.forEach(clearInterval);
    cleanups.forEach(function (fn) { try { fn(); } catch (e) {} });
    document.documentElement.classList.remove('llh-lock');
    root.__llhLive = false;
    window.__llhTeardown = null;
  };

  var mq = function (q) { return window.matchMedia ? window.matchMedia(q) : { matches: false, addEventListener: function () {} }; };
  var mqPhone = mq('(max-width: 767.98px)');
  var mqDesk = mq('(min-width: 1200px)');
  var mqHover = mq('(hover: hover) and (pointer: fine)');
  var mqCalm = mq('(prefers-reduced-motion: reduce)');
  var onChange = function (m, fn) {
    if (m.addEventListener) on(m, 'change', fn);
    else if (m.addListener) { m.addListener(fn); cleanups.push(function () { m.removeListener(fn); }); }
  };

  var $ = function (sel, el) { return (el || document).querySelector(sel); };
  var $$ = function (sel, el) { return Array.prototype.slice.call((el || document).querySelectorAll(sel)); };
  var svgNS = 'http://www.w3.org/2000/svg';
  var icon = function (d, cls) {
    var s = document.createElementNS(svgNS, 'svg');
    s.setAttribute('viewBox', '0 0 24 24');
    s.setAttribute('aria-hidden', 'true');
    if (cls) s.setAttribute('class', cls);
    d.split('|').forEach(function (p) {
      var path = document.createElementNS(svgNS, 'path');
      path.setAttribute('d', p);
      s.appendChild(path);
    });
    return s;
  };
  var ICON = {
    chev: 'm10 7 5 5-5 5',
    back: 'm14 6-6 6 6 6',
    arrow: 'M5 12h13|M13 6l6 6-6 6',
    search: 'M16.2 16.2 20.3 20.3|M11 4.4a6.6 6.6 0 1 0 0 13.2 6.6 6.6 0 0 0 0-13.2Z',
    clock: 'M12 7v5l3 2|M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17Z',
    grid: 'M4.5 4.5h6v6h-6z|M13.5 4.5h6v6h-6z|M4.5 13.5h6v6h-6z|M13.5 13.5h6v6h-6z',
    card: 'M7 3.5h10a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2Z|M8.5 8h7M8.5 11.5h7'
  };
  var el = function (tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  };
  var store = {
    get: function (area, key) { try { return window[area].getItem(key); } catch (e) { return null; } },
    set: function (area, key, v) { try { window[area].setItem(key, v); } catch (e) {} },
    del: function (area, key) { try { window[area].removeItem(key); } catch (e) {} }
  };

  var util = $('[data-llh-util]', root);
  var main = $('[data-site-header-main]', root);
  var rail = $('[data-site-navigation]', root);
  var drawer = document.getElementById('llh-drawer');

  var state = { panelOpen: false, searchOpen: false, drawerOpen: false };

  /* ------------------------------------------------------------------------
     1. Sticky
     The header is position: sticky with a negative top the height of the
     utility strip, so the strip scrolls away and the bar and rail stay. On
     the way down the whole thing tucks up out of sight; any scroll up brings
     it back. Phones never stick: the bottom tab bar is their navigation.
     ------------------------------------------------------------------------ */
  var sticky = root.hasAttribute('data-llh-sticky');
  var sentinel = document.createElement('div');
  sentinel.setAttribute('aria-hidden', 'true');
  sentinel.style.cssText = 'height:0;margin:0;padding:0;border:0;';
  root.parentNode.insertBefore(sentinel, root);
  cleanups.push(function () { if (sentinel.parentNode) sentinel.parentNode.removeChild(sentinel); });

  var tucked = false;
  var stuck = false;
  var lastY = window.pageYOffset;
  var travel = 0;
  var lastPublished = -1;
  var headerH = 0;
  var utilH = 0;

  var measure = function () {
    headerH = root.offsetHeight;
    utilH = util ? util.offsetHeight : 0;
    root.style.setProperty('--llh-h', headerH + 'px');
  };

  var publish = function () {
    var canStick = sticky && !mqPhone.matches;
    var h = canStick && !tucked ? Math.max(0, headerH - utilH) : 0;
    if (h !== lastPublished) {
      document.documentElement.style.setProperty('--header-height', h + 'px');
      lastPublished = h;
    }
  };

  var setTucked = function (on) {
    if (on === tucked) return;
    tucked = on;
    root.classList.toggle('is-tucked', on);
    publish();
  };

  var holdOpen = function () {
    var a = document.activeElement;
    return state.panelOpen || state.searchOpen || state.drawerOpen ||
      !!(a && a.matches && a.matches('.llh-search__input'));
  };

  var onScroll = function () {
    var y = Math.max(0, window.pageYOffset);
    var canStick = sticky && !mqPhone.matches;
    var top = sentinel.getBoundingClientRect().top + y;

    if (!canStick) {
      setTucked(false);
      if (stuck) { stuck = false; root.classList.remove('is-stuck'); }
      lastY = y;
      publish();
      return;
    }

    var nowStuck = y > top + utilH + 1;
    if (nowStuck !== stuck) { stuck = nowStuck; root.classList.toggle('is-stuck', stuck); }

    var dy = y - lastY;
    lastY = y;
    if ((dy > 0 && travel < 0) || (dy < 0 && travel > 0)) travel = 0;
    travel += dy;

    if (y < top + headerH + 40 || holdOpen()) setTucked(false);
    else if (travel > 14) setTucked(true);
    else if (travel < -8) setTucked(false);
    publish();
  };

  var scrollQueued = false;
  on(window, 'scroll', function () {
    if (scrollQueued) return;
    scrollQueued = true;
    requestAnimationFrame(function () { scrollQueued = false; onScroll(); });
  }, { passive: true });

  var relayout = function () { measure(); onScroll(); publish(); };
  if (window.ResizeObserver) {
    var ro = new ResizeObserver(relayout);
    ro.observe(root);
    cleanups.push(function () { ro.disconnect(); });
  }
  on(window, 'resize', relayout, { passive: true });
  onChange(mqPhone, relayout);
  /* A ResizeObserver delivers its first callback after the browser's own
     layout and before paint, so relayout() above already measures in time for
     the first frame. Measuring here as well forced a synchronous layout while
     the rest of the page was still being parsed (~520 ms in PageSpeed). Only
     browsers without ResizeObserver need the immediate pass. */
  if (!window.ResizeObserver) {
    measure();
    publish();
    onScroll();
  }

  /* ------------------------------------------------------------------------
     2. Utility ticker
     Desktop shows every promise in a row. Below that they roll, one at a
     time, pausing while a pointer or focus is on them.
     ------------------------------------------------------------------------ */
  (function () {
    var ticker = $('[data-llh-ticker]', root);
    if (!ticker) return;
    var items = $$('.llh-ticker__item', ticker);
    if (items.length < 2) return;
    var i = 0;
    var timer = null;
    var paused = false;

    var show = function (next) {
      var cur = items[i];
      if (cur === items[next]) return;
      cur.classList.remove('is-on');
      cur.classList.add('is-out');
      setTimeout(function () { cur.classList.remove('is-out'); }, 650);
      i = next;
      items[i].classList.add('is-on');
    };
    var tick = function () { if (!paused && !document.hidden) show((i + 1) % items.length); };
    /* Desktop: as many promises as fit beside the help links, dropped from
       the end, so a longer promise or a narrower window never collides. */
    var fit = function () {
      items.forEach(function (it) { it.classList.remove('is-cut'); });
      if (!mqDesk.matches) return;
      for (var n = items.length - 1; n > 0 && ticker.scrollWidth > ticker.clientWidth + 1; n--) {
        items[n].classList.add('is-cut');
      }
    };
    on(window, 'resize', fit, { passive: true });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit);
    var start = function () {
      clearInterval(timer);
      fit();
      if (mqDesk.matches) {
        ticker.classList.remove('is-live');
        items.forEach(function (it) { it.classList.remove('is-on', 'is-out'); });
        return;
      }
      ticker.classList.add('is-live');
      items.forEach(function (it, n) { it.classList.toggle('is-on', n === i); });
      timer = every(tick, 4200);
    };
    ticker.addEventListener('pointerenter', function () { paused = true; });
    ticker.addEventListener('pointerleave', function () { paused = false; });
    ticker.addEventListener('focusin', function () { paused = true; });
    ticker.addEventListener('focusout', function () { paused = false; });
    onChange(mqDesk, start);
    start();
  }());

  /* ------------------------------------------------------------------------
     3. Search
     ------------------------------------------------------------------------ */
  (function () {
    var wrap = $('[data-llh-search]', root);
    if (!wrap) return;
    var form = $('[data-llh-search-form]', wrap);
    var input = $('[data-llh-search-input]', wrap);
    var clearBtn = $('[data-llh-search-clear]', wrap);
    var closeBtn = $('[data-llh-search-close]', wrap);
    var panel = $('[data-llh-suggest]', wrap);
    var results = $('[data-llh-suggest-results]', wrap);
    var status = $('[data-llh-suggest-status]', wrap);
    var recentBlock = $('[data-llh-recent-block]', wrap);
    var recentList = $('[data-llh-recent]', wrap);
    var trending = $('[data-llh-trending]', wrap);
    if (!form || !input || !panel || !results) return;

    var RECENT_KEY = 'll_recent_searches';
    var MIN = 2;
    var searchUrl = form.getAttribute('action') || '/search';
    var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
    var money;
    try { money = new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency }); }
    catch (e) { money = { format: function (n) { return '£' + Number(n).toFixed(2); } }; }

    var hints = [];
    try { hints = JSON.parse(($('[data-llh-hints]', wrap) || {}).textContent || '[]'); } catch (e) { hints = []; }
    hints = hints.filter(function (h) { return typeof h === 'string' && h.trim(); });

    var queryUrl = function (q) {
      return searchUrl + '?q=' + encodeURIComponent(q) + '&type=product&options%5Bunavailable_products%5D=last&options%5Bprefix%5D=last';
    };

    /* ---- rolling placeholder ---- */
    var ph = null;
    var roll = null;
    var hintIndex = 0;
    $$('.llh-search__ph', wrap).forEach(function (n) { n.parentNode.removeChild(n); });
    if (hints.length && !mqCalm.matches) {
      ph = el('span', 'llh-search__ph');
      ph.setAttribute('aria-hidden', 'true');
      ph.appendChild(el('span', null, 'Search'));
      roll = el('span', 'llh-search__ph-roll');
      roll.appendChild(el('span', null, hints[0]));
      ph.appendChild(roll);
      input.insertAdjacentElement('afterend', ph);
      wrap.classList.add('has-ph');
      every(function () {
        if (document.hidden || input.value) return;
        hintIndex = (hintIndex + 1) % hints.length;
        var old = roll.lastElementChild;
        var next = el('span', 'is-in', hints[hintIndex]);
        roll.appendChild(next);
        void next.offsetWidth;
        next.classList.remove('is-in');
        if (old) {
          old.classList.add('is-out');
          setTimeout(function () { if (old.parentNode) old.parentNode.removeChild(old); }, 650);
        }
      }, 3400);
    }

    /* ---- recent and trending ---- */
    var readRecent = function () {
      try {
        var v = JSON.parse(store.get('localStorage', RECENT_KEY) || '[]');
        return Object.prototype.toString.call(v) === '[object Array]' ? v.filter(function (x) { return typeof x === 'string'; }) : [];
      } catch (e) { return []; }
    };
    var pushRecent = function (q) {
      q = String(q || '').trim();
      if (q.length < MIN) return;
      var list = readRecent().filter(function (x) { return x.toLowerCase() !== q.toLowerCase(); });
      list.unshift(q);
      store.set('localStorage', RECENT_KEY, JSON.stringify(list.slice(0, 6)));
    };
    var chip = function (text, href, iconPath) {
      var li = el('li');
      var a = el('a', 'llh-chip');
      a.href = href;
      if (iconPath) a.appendChild(icon(iconPath));
      a.appendChild(el('span', null, text));
      li.appendChild(a);
      return li;
    };
    var paintRecent = function () {
      if (!recentBlock || !recentList) return;
      var list = readRecent();
      recentList.textContent = '';
      list.forEach(function (q) { recentList.appendChild(chip(q, queryUrl(q), ICON.clock)); });
      recentBlock.hidden = list.length === 0;
    };
    var clearRecent = $('[data-llh-recent-clear]', wrap);
    if (clearRecent) clearRecent.addEventListener('click', function () {
      store.del('localStorage', RECENT_KEY);
      paintRecent();
      input.focus();
    });
    if (trending) {
      hints.slice(0, 6).forEach(function (h) { trending.appendChild(chip(h, queryUrl(h))); });
      if (!hints.length && trending.parentNode) trending.parentNode.hidden = true;
    }

    /* ---- open and close ---- */
    var closeTimer = null;
    var open = function () {
      if (state.searchOpen) return;
      clearTimeout(closeTimer);
      state.searchOpen = true;
      closePanels();
      paintRecent();
      panel.hidden = false;
      void panel.offsetWidth;
      panel.classList.add('is-open');
      wrap.classList.add('is-focus');
      input.setAttribute('aria-expanded', 'true');
    };
    var close = function () {
      if (!state.searchOpen) return;
      state.searchOpen = false;
      panel.classList.remove('is-open');
      wrap.classList.remove('is-focus');
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      closeTimer = setTimeout(function () { if (!state.searchOpen) panel.hidden = true; }, 220);
      if (wrap.classList.contains('is-takeover')) {
        wrap.classList.remove('is-takeover');
        document.documentElement.classList.remove('llh-lock');
      }
    };

    /* ---- predictive search ---- */
    var cache = {};
    var controller = null;
    var timer = null;
    var skelTimer = null;
    var active = -1;
    var lastQ = '';

    var terms = function (q) {
      return q.toLowerCase().split(/\s+/).filter(function (t) { return t.length > 0; }).sort(function (a, b) { return b.length - a.length; });
    };
    /* Wraps each occurrence of a search term in <mark>, built as nodes. */
    var highlight = function (node, text, q) {
      var ts = terms(q);
      if (!ts.length) { node.textContent = text; return; }
      var lower = text.toLowerCase();
      var i = 0;
      while (i < text.length) {
        var hit = -1, len = 0;
        for (var t = 0; t < ts.length; t++) {
          var at = lower.indexOf(ts[t], i);
          if (at !== -1 && (hit === -1 || at < hit)) { hit = at; len = ts[t].length; }
        }
        if (hit === -1) { node.appendChild(document.createTextNode(text.slice(i))); break; }
        if (hit > i) node.appendChild(document.createTextNode(text.slice(i, hit)));
        node.appendChild(el('mark', null, text.slice(hit, hit + len)));
        i = hit + len;
      }
    };

    var priceOf = function (p) {
      var min = parseFloat(p.price_min != null ? p.price_min : p.price);
      var max = parseFloat(p.price_max != null ? p.price_max : p.price);
      var was = parseFloat(p.compare_at_price_max || 0);
      if (isNaN(min)) return null;
      return { text: (max > min ? 'From ' : '') + money.format(min), was: was > min ? money.format(was) : '', sale: was > min };
    };

    var options = function () { return $$('[role="option"]', results); };
    var setActive = function (n) {
      var opts = options();
      opts.forEach(function (o) { o.setAttribute('aria-selected', 'false'); });
      if (n < 0 || n >= opts.length) { active = -1; input.removeAttribute('aria-activedescendant'); return; }
      active = n;
      opts[n].setAttribute('aria-selected', 'true');
      input.setAttribute('aria-activedescendant', opts[n].id);
      opts[n].scrollIntoView({ block: 'nearest' });
    };

    var optId = 0;
    var option = function (href, cls) {
      var a = el('a', 'llh-opt ' + (cls || ''));
      a.href = href;
      a.id = 'llh-opt-' + (++optId);
      a.setAttribute('role', 'option');
      a.setAttribute('aria-selected', 'false');
      a.tabIndex = -1;
      return a;
    };

    var render = function (q, data) {
      results.textContent = '';
      active = -1;
      input.removeAttribute('aria-activedescendant');
      var r = (data && data.resources && data.resources.results) || {};
      var products = (r.products || []).slice(0, 6);
      var collections = (r.collections || []).slice(0, 3);
      var queries = (r.queries || []).filter(function (x) { return x && x.text && x.text.toLowerCase() !== q.toLowerCase(); }).slice(0, 3);

      if (!products.length && !collections.length && !queries.length) {
        var empty = el('p', 'llh-suggest__empty');
        empty.appendChild(document.createTextNode('No matches for '));
        empty.appendChild(el('b', null, '“' + q + '”'));
        empty.appendChild(document.createTextNode('. Try a set name, a card name or a card number.'));
        results.appendChild(empty);
        panel.classList.add('has-results');
        if (status) status.textContent = 'No suggestions';
        return;
      }

      if (queries.length) {
        var g1 = el('div', 'llh-sgroup');
        g1.setAttribute('role', 'group');
        g1.setAttribute('aria-label', 'Suggestions');
        g1.appendChild(el('p', 'llh-sgroup__title', 'Suggestions')).setAttribute('aria-hidden', 'true');
        queries.forEach(function (x) {
          var a = option(queryUrl(x.text), 'llh-opt--query');
          a.appendChild(icon(ICON.search));
          var m = el('span', 'llh-opt__main');
          highlight(m, x.text, q);
          a.appendChild(m);
          g1.appendChild(a);
        });
        results.appendChild(g1);
      }

      if (collections.length) {
        var g2 = el('div', 'llh-sgroup');
        g2.setAttribute('role', 'group');
        g2.setAttribute('aria-label', 'Collections');
        g2.appendChild(el('p', 'llh-sgroup__title', 'Collections')).setAttribute('aria-hidden', 'true');
        collections.forEach(function (c) {
          var a = option(c.url, 'llh-opt--collection');
          a.appendChild(icon(ICON.grid));
          var m = el('span', 'llh-opt__main');
          highlight(m, c.title || '', q);
          a.appendChild(m);
          g2.appendChild(a);
        });
        results.appendChild(g2);
      }

      if (products.length) {
        var g3 = el('div', 'llh-sgroup');
        g3.setAttribute('role', 'group');
        g3.setAttribute('aria-label', 'Products');
        g3.appendChild(el('p', 'llh-sgroup__title', 'Products')).setAttribute('aria-hidden', 'true');
        products.forEach(function (p) {
          var a = option(p.url, 'llh-opt--product');
          var src = (p.featured_image && p.featured_image.url) || p.image;
          var th = el('span', 'llh-opt__thumb' + (src ? '' : ' llh-opt__thumb--none'));
          if (src) {
            var img = document.createElement('img');
            img.alt = '';
            img.loading = 'lazy';
            img.decoding = 'async';
            img.width = 56;
            img.height = 56;
            img.src = src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=120';
            th.appendChild(img);
          } else {
            th.appendChild(icon(ICON.card));
          }
          a.appendChild(th);
          var body = el('span', 'llh-opt__body');
          var m = el('span', 'llh-opt__main');
          highlight(m, p.title || '', q);
          body.appendChild(m);
          var meta = el('span', 'llh-opt__meta');
          var plain = function (t) { return String(t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase(); };
          var bits = [p.vendor, p.type].filter(function (b, n, all) {
            return b && String(b).trim() && (n === 0 || plain(b) !== plain(all[0]));
          });
          if (bits.length) meta.appendChild(el('span', null, bits.join(' · ')));
          var pr = priceOf(p);
          if (p.available === false) meta.appendChild(el('span', 'llh-flag llh-flag--out', 'Sold out'));
          else if (pr && pr.sale) meta.appendChild(el('span', 'llh-flag llh-flag--sale', 'Sale'));
          body.appendChild(meta);
          a.appendChild(body);
          if (pr) {
            var price = el('span', 'llh-opt__price' + (pr.sale ? ' llh-opt__price--sale' : ''), pr.text);
            if (pr.was) price.appendChild(el('s', null, pr.was));
            a.appendChild(price);
          }
          g3.appendChild(a);
        });
        results.appendChild(g3);
      }

      var foot = el('div', 'llh-suggest__foot');
      foot.setAttribute('role', 'group');
      foot.setAttribute('aria-label', 'All results');
      var all = option(queryUrl(q), 'llh-suggest__all');
      all.className = 'llh-suggest__all';
      all.appendChild(el('span', null, 'See all results for “' + q + '”'));
      all.appendChild(icon(ICON.arrow));
      foot.appendChild(all);
      results.appendChild(foot);

      panel.classList.add('has-results');
      if (status) {
        var n = products.length + collections.length + queries.length;
        status.textContent = n + (n === 1 ? ' suggestion' : ' suggestions') + ', use the arrow keys to move through them';
      }
    };

    var showIdle = function () {
      clearTimeout(timer);
      clearTimeout(skelTimer);
      if (controller) { try { controller.abort(); } catch (e) {} controller = null; }
      results.textContent = '';
      panel.classList.remove('has-results');
      active = -1;
      lastQ = '';
      input.removeAttribute('aria-activedescendant');
    };

    var fetchSuggest = function (q) {
      lastQ = q;
      if (cache[q]) { render(q, cache[q]); return; }
      if (controller) { try { controller.abort(); } catch (e) {} }
      controller = window.AbortController ? new AbortController() : null;
      clearTimeout(skelTimer);
      skelTimer = setTimeout(function () {
        if (lastQ !== q || cache[q]) return;
        results.textContent = '';
        var sk = el('div', 'llh-skel');
        for (var i = 0; i < 4; i++) sk.appendChild(el('i'));
        results.appendChild(sk);
        panel.classList.add('has-results');
      }, 160);
      var url = rootUrl + 'search/suggest.json?q=' + encodeURIComponent(q) +
        '&resources[type]=product,collection,query&resources[limit]=6&resources[limit_scope]=each' +
        '&resources[options][unavailable_products]=last' +
        '&resources[options][fields]=title,product_type,variants.title,vendor,tag';
      fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: controller ? controller.signal : undefined })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
        .then(function (data) {
          cache[q] = data;
          clearTimeout(skelTimer);
          if (lastQ === q && input.value.trim() === q) render(q, data);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          clearTimeout(skelTimer);
          if (lastQ === q) showIdle();
        });
    };

    var onInput = function () {
      var q = input.value.trim();
      wrap.classList.toggle('has-value', input.value.length > 0);
      if (clearBtn) clearBtn.hidden = input.value.length === 0;
      if (!state.searchOpen) open();
      clearTimeout(timer);
      if (q.length < MIN) { showIdle(); return; }
      timer = setTimeout(function () { fetchSuggest(q); }, 140);
    };

    input.addEventListener('input', onInput);
    input.addEventListener('focus', function () {
      if (phoneMode()) return;
      open();
      if (input.value.trim().length >= MIN && !panel.classList.contains('has-results')) fetchSuggest(input.value.trim());
    });
    input.addEventListener('keydown', function (e) {
      var opts = options();
      if (e.key === 'ArrowDown') {
        if (!state.searchOpen) open();
        if (opts.length) { e.preventDefault(); setActive(active + 1 >= opts.length ? 0 : active + 1); }
      } else if (e.key === 'ArrowUp') {
        if (opts.length) { e.preventDefault(); setActive(active <= 0 ? opts.length - 1 : active - 1); }
      } else if (e.key === 'Enter') {
        if (active > -1 && opts[active]) { e.preventDefault(); pushRecent(input.value); opts[active].click(); }
      } else if (e.key === 'Escape') {
        if (input.value && state.searchOpen) { input.value = ''; onInput(); }
        else { close(); input.blur(); }
      }
    });
    if (clearBtn) clearBtn.addEventListener('click', function () {
      input.value = '';
      onInput();
      input.focus();
    });
    if (closeBtn) closeBtn.addEventListener('click', function () { close(); input.blur(); });

    var hiddenQ = $('[data-llh-search-q]', form);
    on(form, 'submit', function (e) {
      var q = input.value.trim();
      if (!q) { e.preventDefault(); input.focus(); return; }
      if (hiddenQ) hiddenQ.value = q;
      pushRecent(q);
    });
    results.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('a')) pushRecent(input.value);
    });

    on(document, 'pointerdown', function (e) {
      if (state.searchOpen && !wrap.contains(e.target)) close();
    });
    wrap.addEventListener('focusout', function (e) {
      var to = e.relatedTarget;
      if (to && wrap.contains(to)) return;
      setTimeout(function () {
        if (!wrap.contains(document.activeElement)) close();
      }, 0);
    });

    /* ---- phones ----
       The bottom tab bar already owns a full-screen search sheet. Tapping the
       pill opens that same sheet, so a phone has one search, not two. The
       field is read-only there so iOS does not raise its keyboard for a field
       that is about to be replaced. Without the sheet, the field takes over
       the screen itself. */
    var sheetTab = function () {
      var sheet = document.getElementById('ll-search-sheet');
      return sheet ? document.querySelector('.llmt__tab[href*="/search"]') : null;
    };
    var phoneMode = function () { return mqPhone.matches; };
    var syncPhone = function () {
      if (phoneMode() && sheetTab()) input.setAttribute('readonly', '');
      else input.removeAttribute('readonly');
    };
    syncPhone();
    onChange(mqPhone, function () { syncPhone(); if (!phoneMode()) wrap.classList.remove('is-takeover'); });

    form.addEventListener('click', function (e) {
      if (!phoneMode()) return;
      if (wrap.classList.contains('is-takeover')) return;
      if (e.target.closest && e.target.closest('[data-llh-search-close]')) return;
      var tab = sheetTab();
      e.preventDefault();
      if (tab) {
        input.blur();
        tab.click();
        return;
      }
      wrap.classList.add('is-takeover');
      document.documentElement.classList.add('llh-lock');
      input.removeAttribute('readonly');
      open();
      input.focus();
    });

    /* "/" focuses search on desktop, as on most stores. */
    on(document, 'keydown', function (e) {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      var t = e.target;
      if (t && (t.isContentEditable || /^(input|textarea|select)$/i.test(t.tagName))) return;
      if (phoneMode()) return;
      e.preventDefault();
      input.focus();
    });

    if (input.value) wrap.classList.add('has-value');
    if (clearBtn) clearBtn.hidden = input.value.length === 0;
    root.__llhSearchClose = close;
  }());

  /* ------------------------------------------------------------------------
     4. Game rail and panels
     ------------------------------------------------------------------------ */
  var panelsBox = rail ? $('[data-llh-panels]', rail) : null;
  var scrim = $('[data-llh-scrim]', root);
  var ink = rail ? $('[data-llh-ink]', rail) : null;
  var railIn = rail ? $('.llh-rail__in', rail) : null;
  var scroller = rail ? $('[data-llh-rail-scroller]', rail) : null;
  var tabs = rail ? $$('[data-llh-tab]', rail) : [];
  var current = null;
  var openTimer = null;
  var closeTimer = null;
  var leaveTimer = null;

  var panelFor = function (tab) {
    var t = tab && $('[data-llh-tab-toggle]', tab);
    return t ? document.getElementById(t.getAttribute('aria-controls')) : null;
  };
  var colorOf = function (tab) {
    return tab ? getComputedStyle(tab).getPropertyValue('--g').trim() : '';
  };

  var moveInk = function (tab, instant) {
    if (!ink || !railIn) return;
    var target = tab || $('.llh-tab.is-current', rail);
    if (!target || mqPhone.matches) { ink.classList.remove('is-on'); return; }
    var a = target.getBoundingClientRect();
    var b = railIn.getBoundingClientRect();
    var inset = 10;
    var x = a.left - b.left + inset;
    var w = Math.max(12, a.width - inset * 2);
    if (instant) ink.style.transition = 'none';
    ink.style.transform = 'translate3d(' + x + 'px,0,0)';
    ink.style.width = w + 'px';
    ink.style.setProperty('--ink', colorOf(target));
    ink.classList.toggle('is-on', !!tab || target.classList.contains('is-current'));
    ink.style.opacity = tab ? '' : '.55';
    if (instant) { void ink.offsetWidth; ink.style.transition = ''; }
  };

  var sizePanels = function (instant) {
    if (!panelsBox || !current) return;
    var p = panelFor(current);
    if (!p) return;
    if (instant) panelsBox.classList.add('no-anim');
    panelsBox.style.setProperty('--llh-panels-h', p.offsetHeight + 'px');
    if (instant) { void panelsBox.offsetWidth; panelsBox.classList.remove('no-anim'); }
  };

  var eagerArt = function () {
    if (eagerArt.done || !panelsBox) return;
    eagerArt.done = true;
    $$('img[data-llh-art]', panelsBox).forEach(function (img) { img.loading = 'eager'; });
  };

  var loadLatest = function (panel) {
    var box = $('[data-llh-latest]', panel);
    if (!box || box.getAttribute('data-state')) return;
    box.setAttribute('data-state', 'loading');
    var list = $('[data-llh-latest-list]', box);
    var base = box.getAttribute('data-llh-latest');
    var key = 'llh:latest:v2:' + base;
    var put = function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var out = doc.querySelector('[data-llh-latest-out]');
      if (!out || !out.querySelector('a')) { box.hidden = true; box.setAttribute('data-state', 'empty'); sizePanels(); return; }
      var limit = parseInt(box.getAttribute('data-llh-limit'), 10) || 2;
      list.textContent = '';
      Array.prototype.slice.call(out.children, 0, limit).forEach(function (n) { list.appendChild(document.importNode(n, true)); });
      box.setAttribute('data-state', 'ready');
      sizePanels();
    };
    try {
      var hit = JSON.parse(store.get('sessionStorage', key) || 'null');
      if (hit && Date.now() - hit.t < 10 * 60 * 1000 && hit.h) { put(hit.h); return; }
    } catch (e) {}
    var url = base + (base.indexOf('?') > -1 ? '&' : '?') + 'sort_by=created-descending&section_id=llh-latest';
    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
      .then(function (html) {
        store.set('sessionStorage', key, JSON.stringify({ t: Date.now(), h: html }));
        put(html);
      })
      .catch(function () { box.hidden = true; box.setAttribute('data-state', 'failed'); sizePanels(); });
  };

  /* ---- feature showcase: Coming soon and Accessories ----
     Their feature has no picture of its own, so it shows real products: the next
     releases, soonest first (by the shopper's own date, release day reads "Out
     today"), or the best sellers that are in stock. One product at a time,
     crossfading every few seconds; the picture links to the product on screen.
     Paused while the pointer or focus is on the feature, stopped while the panel
     is closed, and still under reduced motion. */
  var SHOW_MS = 3400;
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var todayKey = function () {
    var d = new Date();
    return d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2);
  };
  var dayDiff = function (ymd) {
    var p = String(ymd).split('-');
    var now = new Date();
    var a = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    var b = new Date(+p[0], +p[1] - 1, +p[2]);
    return Math.round((b - a) / 864e5);
  };
  var shortDate = function (ymd) {
    var p = String(ymd).split('-');
    return (+p[2]) + ' ' + MONTHS[+p[1] - 1];
  };
  /* "Pokemon : 30th Celebration - Figure Collection - Mew" reads as the product. */
  var cleanTitle = function (t) {
    return String(t || '').replace(/：/g, ':').replace(/^\s*Pok[eé]mon\s*:\s*/i, '')
      .replace(/\s-\s\(/g, ' (').replace(/\s:\s/g, ' · ').replace(/\s-\s/g, ' · ').trim();
  };
  var stemOf = function (t) {
    var s = String(t || '').toLowerCase();
    var i = s.lastIndexOf(' - ');
    return i > 0 ? s.slice(0, i) : s;
  };
  var pickShow = function (items, mode) {
    var out = [], seen = {};
    var list = items.slice();
    if (mode === 'soon') {
      var today = todayKey();
      list = list.filter(function (x) { return x.r && x.r >= today; });
      /* Soonest first; the collection's own order breaks ties. */
      list = list.map(function (x, i) { x._i = i; return x; }).sort(function (a, b) { return a.r < b.r ? -1 : a.r > b.r ? 1 : a._i - b._i; });
    } else {
      list = list.filter(function (x) { return x.a; });
    }
    for (var i = 0; i < list.length && out.length < 6; i++) {
      var st = stemOf(list[i].t);
      if (seen[st]) continue;
      seen[st] = 1;
      out.push(list[i]);
    }
    return out;
  };

  /* The products behind a showcase: cached for ten minutes in the session, and
     one request at a time per showcase however many boxes ask for it (the
     desktop panel, the phone Shop sheet, the warm-up). */
  var showWait = {};
  var showData = function (mode, base) {
    var key = 'llh:show:v1:' + mode + ':' + base;
    try {
      var hit = JSON.parse(store.get('sessionStorage', key) || 'null');
      if (hit && Date.now() - hit.t < 10 * 60 * 1000 && hit.d) return Promise.resolve(hit.d);
    } catch (e) {}
    if (showWait[key]) return showWait[key];
    var url = base + (base.indexOf('?') > -1 ? '&' : '?') + (mode === 'best' ? 'sort_by=best-selling&' : '') + 'section_id=llh-feature-feed';
    /* One retry after a moment: a phone on a patchy connection can lose the
       first request, and without it the feature would wait for the next open. */
    var attempt = function (left) {
      return fetch(url, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
        .catch(function (err) {
          if (!left) throw err;
          return new Promise(function (res) { setTimeout(res, 1200); }).then(function () { return attempt(left - 1); });
        });
    };
    showWait[key] = attempt(1)
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var el = doc.querySelector('[data-llh-feed]');
        var data = el ? JSON.parse(el.textContent) : [];
        store.set('sessionStorage', key, JSON.stringify({ t: Date.now(), d: data }));
        delete showWait[key];
        return data;
      }, function (err) { delete showWait[key]; throw err; });
    return showWait[key];
  };

  /* Runs one showcase box: anything holding [data-llh-show] with the stage,
     link, kicker, meta and counter hooks inside it. isLive() says whether it
     is on screen right now; it only moves while that is true. Returns
     { play, stop }, usable before the products have arrived. */
  var startShow = function (box, isLive) {
    var api = {
      play: function () { if (box.__show) box.__show.play(); },
      stop: function () { if (box.__show) box.__show.stop(); }
    };
    if (box.__show) { box.__show.play(); return api; }
    if (box.getAttribute('data-state')) return api;
    box.setAttribute('data-state', 'loading');
    var mode = box.getAttribute('data-llh-show-mode');
    var base = box.getAttribute('data-llh-show');

    var build = function (items) {
      var picks = pickShow(items, mode);
      if (!picks.length) { box.setAttribute('data-state', 'empty'); return; }
      var stage = $('[data-llh-show-stage]', box);
      var link = $('[data-llh-show-link]', box);
      var kicker = $('[data-llh-show-kicker]', box);
      var meta = $('[data-llh-show-meta]', box);
      var count = $('[data-llh-show-n]', box);
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      stage.textContent = '';
      var slides = picks.map(function (x, i) {
        var s = document.createElement('span');
        s.className = 'llh-show__slide';
        var img = document.createElement('img');
        img.src = x.i;
        img.alt = '';
        img.decoding = 'async';
        img.loading = i === 0 ? 'eager' : 'lazy';
        img.width = 240;
        img.height = 240;
        s.appendChild(img);
        var chip = document.createElement('span');
        chip.className = 'llh-show__chip';
        if (mode === 'soon') {
          var dd = dayDiff(x.r);
          chip.textContent = dd <= 0 ? 'Out today' : dd === 1 ? 'Out tomorrow' : shortDate(x.r);
        } else {
          chip.textContent = (x.f ? 'From ' : '') + x.p;
        }
        if (chip.textContent) s.appendChild(chip);
        stage.appendChild(s);
        return s;
      });
      box.classList.add('is-showing');

      var at = -1, timer = null, held = false;
      var show = function (i) {
        at = (i + slides.length) % slides.length;
        slides.forEach(function (s, j) { s.classList.toggle('is-on', j === at); });
        var x = picks[at];
        link.href = x.u;
        link.setAttribute('aria-label', x.t);
        if (kicker) {
          if (mode === 'soon') {
            var dd = dayDiff(x.r);
            kicker.textContent = dd <= 0 ? 'Out today' : dd === 1 ? 'Releases tomorrow' : 'Releases ' + shortDate(x.r) + (dd <= 30 ? ' · in ' + dd + ' days' : '');
          } else {
            kicker.textContent = at === 0 ? 'Our best seller' : 'Best seller';
          }
        }
        if (meta) {
          meta.textContent = cleanTitle(x.t) + (mode === 'best' || !x.p ? '' : ' · ' + x.p);
          meta.title = x.t;
        }
        if (count) { count.hidden = slides.length < 2; count.textContent = (at + 1) + ' / ' + slides.length; }
      };
      var stop = function () { clearTimeout(timer); timer = null; };
      var tick = function () {
        stop();
        if (reduce || held || slides.length < 2) return;
        timer = setTimeout(function () {
          /* Only move while it is actually on screen. */
          if (!isLive()) { stop(); return; }
          show(at + 1);
          tick();
        }, SHOW_MS);
      };
      var hold = function (on) { held = on; if (on) stop(); else tick(); };
      box.addEventListener('pointerenter', function () { hold(true); });
      box.addEventListener('pointerleave', function () { hold(false); });
      box.addEventListener('focusin', function () { hold(true); });
      box.addEventListener('focusout', function (e) { if (!box.contains(e.relatedTarget)) hold(false); });

      box.__show = { play: function () { if (at < 0) show(0); tick(); }, stop: stop };
      show(0);
      box.setAttribute('data-state', 'ready');
      if (isLive()) tick();
    };

    showData(mode, base)
      .then(build)
      /* A failed fetch leaves the nut and tries again the next time it opens. */
      .catch(function () { box.removeAttribute('data-state'); });
    return api;
  };
  var loadShow = function (panel) {
    var box = $('[data-llh-show]', panel);
    if (!box) return;
    startShow(box, function () { return panel.classList.contains('is-active') && !panel.hidden; });
  };
  /* The phone tab bar's Shop sheet builds its own copy of these features and
     runs them through the same code (assets/llmt-tabbar.js). */
  window.LLH = window.LLH || {};
  window.LLH.show = startShow;
  window.LLH.warmShow = function (box) {
    if (box) showData(box.getAttribute('data-llh-show-mode'), box.getAttribute('data-llh-show')).catch(function () {});
  };
  /* Warm the showcases as soon as the shopper reaches for the menu, so the first
     products are already there when a panel opens. Once per page, and only on
     intent: nobody who never touches the menu pays for it. */
  var warmShows = function () {
    if (warmShows.done || !panelsBox) return;
    warmShows.done = true;
    $$('[data-llh-show]', panelsBox).forEach(function (box) {
      var panel = box.closest('[data-llh-panel]');
      if (panel) loadShow(panel);
    });
  };
  if (rail) {
    rail.addEventListener('pointerenter', warmShows, { once: true });
    rail.addEventListener('focusin', warmShows, { once: true });
  }
  var stopShows = function () {
    if (!panelsBox) return;
    $$('[data-llh-show]', panelsBox).forEach(function (b) { if (b.__show) b.__show.stop(); });
  };

  var setEra = function (panel, n) {
    var eras = $$('[data-llh-era]', panel);
    var groups = $$('[data-llh-sets]', panel);
    eras.forEach(function (e) { e.classList.toggle('is-active', e.getAttribute('data-llh-era') === String(n)); });
    groups.forEach(function (g) { g.classList.toggle('is-active', g.getAttribute('data-llh-sets') === String(n)); });
    sizePanels();
  };

  var openPanel = function (tab) {
    var panel = panelFor(tab);
    clearTimeout(closeTimer);
    clearTimeout(openTimer);
    if (!panel || !panelsBox) { closePanels(); return; }
    if (current === tab && state.panelOpen) return;
    if (root.__llhSearchClose) root.__llhSearchClose();

    var prev = current;
    var prevPanel = panelFor(prev);
    var wasOpen = state.panelOpen;
    current = tab;
    state.panelOpen = true;
    setTucked(false);

    tabs.forEach(function (t) {
      var on = t === tab;
      t.classList.toggle('is-open', on);
      var tg = $('[data-llh-tab-toggle]', t);
      if (tg) tg.setAttribute('aria-expanded', on ? 'true' : 'false');
    });

    if (prevPanel && prevPanel !== panel) {
      prevPanel.classList.remove('is-active');
      prevPanel.classList.add('is-leaving');
      setTimeout(function () {
        prevPanel.classList.remove('is-leaving');
        if (current !== prev) prevPanel.hidden = true;
      }, 220);
    }
    panel.hidden = false;
    panel.classList.remove('is-leaving');
    panel.classList.add('is-active');

    var color = colorOf(tab);
    panelsBox.style.setProperty('--ink', color);
    var r = tab.getBoundingClientRect();
    panelsBox.style.setProperty('--glow-x', Math.round((r.left + r.width / 2) / window.innerWidth * 100) + '%');

    sizePanels(!wasOpen);
    panelsBox.classList.add('is-open');
    if (scrim) { scrim.hidden = false; void scrim.offsetWidth; scrim.classList.add('is-on'); }
    moveInk(tab, !wasOpen && !(ink && ink.classList.contains('is-on')));
    eagerArt();
    loadLatest(panel);
    stopShows();
    loadShow(panel);
  };

  var closePanels = function () {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
    if (!state.panelOpen) return;
    stopShows();
    state.panelOpen = false;
    var was = current;
    var wasPanel = panelFor(was);
    current = null;
    tabs.forEach(function (t) {
      t.classList.remove('is-open');
      var tg = $('[data-llh-tab-toggle]', t);
      if (tg) tg.setAttribute('aria-expanded', 'false');
    });
    if (panelsBox) panelsBox.classList.remove('is-open');
    if (scrim) {
      scrim.classList.remove('is-on');
      setTimeout(function () { if (!state.panelOpen) scrim.hidden = true; }, 300);
    }
    setTimeout(function () {
      if (current === null && wasPanel) { wasPanel.classList.remove('is-active'); wasPanel.hidden = true; }
    }, 320);
    moveInk(null);
  };

  if (rail && panelsBox) {
    /* Hover intent. The first panel waits a beat so a pointer crossing the
       rail on its way to the page does not flash a menu; once one is open,
       moving along the rail switches instantly. */
    tabs.forEach(function (tab) {
      tab.addEventListener('pointerenter', function (e) {
        if (e.pointerType !== 'mouse' || !mqHover.matches) return;
        eagerArt();
        clearTimeout(closeTimer);
        clearTimeout(openTimer);
        if (!panelFor(tab)) {
          moveInk(tab);
          if (state.panelOpen) closeTimer = setTimeout(closePanels, 140);
          return;
        }
        openTimer = setTimeout(function () { openPanel(tab); }, state.panelOpen ? 40 : 120);
      });
      tab.addEventListener('pointerleave', function (e) {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(openTimer);
        if (!state.panelOpen) moveInk(null);
      });

      var toggle = $('[data-llh-tab-toggle]', tab);
      if (toggle) {
        toggle.addEventListener('click', function () {
          if (current === tab && state.panelOpen) closePanels();
          else openPanel(tab);
        });
        /* The panels sit after the whole rail in the page, so Tab from an open
           toggle steps into its panel rather than on to the next game, and
           ArrowDown opens it and goes straight in. */
        toggle.addEventListener('keydown', function (e) {
          var inPanel = function () {
            var f = focusables(panelFor(tab));
            if (f[0]) f[0].focus();
          };
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            openPanel(tab);
            setTimeout(inPanel, 30);
          } else if (e.key === 'Tab' && !e.shiftKey && current === tab && state.panelOpen) {
            e.preventDefault();
            inPanel();
          }
        });
      }

      var link = $('.llh-tab__link', tab);
      if (link) {
        var lastPointer = 'mouse';
        link.addEventListener('pointerdown', function (e) { lastPointer = e.pointerType; });
        link.addEventListener('click', function (e) {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;
          var panel = panelFor(tab);
          if (!panel) return;
          if (mqPhone.matches) {
            e.preventDefault();
            openDrawer(link, tab);
            return;
          }
          /* Touch: the first tap opens the panel, a second tap on an open tab
             follows the link. A mouse always follows the link. */
          if (lastPointer !== 'mouse' && !(current === tab && state.panelOpen)) {
            e.preventDefault();
            openPanel(tab);
          }
        });
      }
    });

    var focusables = function (panel) {
      return panel ? $$('a[href], button:not([disabled])', panel).filter(function (n) { return n.offsetParent !== null; }) : [];
    };
    panelsBox.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || !current) return;
      var f = focusables(panelFor(current));
      if (!f.length) return;
      var toggle = $('[data-llh-tab-toggle]', current);
      if (e.shiftKey && document.activeElement === f[0]) {
        e.preventDefault();
        if (toggle) toggle.focus();
      } else if (!e.shiftKey && document.activeElement === f[f.length - 1]) {
        /* Out of the last link: on to the next game in the rail. */
        var next = current.nextElementSibling;
        var target = next ? $('.llh-tab__link', next) : null;
        if (target) { e.preventDefault(); target.focus(); }
      }
    });

    rail.addEventListener('pointerleave', function (e) {
      if (e.pointerType !== 'mouse' || !state.panelOpen) return;
      closeTimer = setTimeout(closePanels, 260);
    });
    rail.addEventListener('pointerenter', function (e) {
      if (e.pointerType === 'mouse') { clearTimeout(closeTimer); eagerArt(); }
    });
    if (scrim) {
      scrim.addEventListener('click', closePanels);
      scrim.addEventListener('pointerenter', function (e) {
        if (e.pointerType === 'mouse' && state.panelOpen) { clearTimeout(closeTimer); closeTimer = setTimeout(closePanels, 160); }
      });
    }
    on(document, 'pointerdown', function (e) {
      if (state.panelOpen && !rail.contains(e.target)) closePanels();
    });
    on(document, 'keydown', function (e) {
      if (e.key !== 'Escape' || !state.panelOpen) return;
      var tg = current && $('[data-llh-tab-toggle]', current);
      closePanels();
      if (tg) tg.focus();
    });
    rail.addEventListener('focusout', function (e) {
      if (!state.panelOpen) return;
      var to = e.relatedTarget;
      if (to && rail.contains(to)) return;
      setTimeout(function () { if (!rail.contains(document.activeElement)) closePanels(); }, 0);
    });

    /* Eras: hover shows the sets beside them after a short pause, so a pointer
       travelling diagonally to a set does not flip through every era on the
       way. Focus switches at once. On touch the first tap on an era with sets
       shows them and the next tap follows the link. */
    var eraTimer = null;
    $$('[data-llh-eras]', panelsBox).forEach(function (box) {
      var panel = box.closest('[data-llh-panel]');
      $$('[data-llh-era]', box).forEach(function (era) {
        var n = era.getAttribute('data-llh-era');
        var group = $('[data-llh-sets="' + n + '"]', panel);
        var hasSets = !!(group && group.querySelector('.llh-set'));
        era.addEventListener('pointerenter', function (e) {
          if (e.pointerType !== 'mouse') return;
          clearTimeout(eraTimer);
          eraTimer = setTimeout(function () { setEra(panel, n); }, 90);
        });
        era.addEventListener('pointerleave', function () { clearTimeout(eraTimer); });
        era.addEventListener('focus', function () { setEra(panel, n); });
        var down = 'mouse';
        era.addEventListener('pointerdown', function (e) { down = e.pointerType; });
        era.addEventListener('click', function (e) {
          if (down === 'mouse' || !hasSets) return;
          if (!era.classList.contains('is-active')) { e.preventDefault(); setEra(panel, n); }
        });
      });
    });

    if (scroller) scroller.addEventListener('scroll', function () {
      if (state.panelOpen) moveInk(current, true); else moveInk(null, true);
    }, { passive: true });
    on(window, 'resize', function () {
      if (mqPhone.matches) closePanels();
      moveInk(state.panelOpen ? current : null, true);
      sizePanels(true);
    }, { passive: true });
    /* Supplies drop off the end of the rail before anything could overlap. */
    var fitRail = function () {
      if (!scroller) return;
      rail.classList.remove('is-tight');
      if (mqDesk.matches && scroller.scrollWidth > scroller.clientWidth + 1) rail.classList.add('is-tight');
    };
    on(window, 'resize', fitRail, { passive: true });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { fitRail(); moveInk(null, true); });
    fitRail();
    moveInk(null, true);

    /* Keep the current game's chip in view on tablets and phones. */
    var cur = $('.llh-tab.is-current', rail);
    if (cur && scroller && scroller.scrollWidth > scroller.clientWidth) {
      var sr = scroller.getBoundingClientRect();
      var cr = cur.getBoundingClientRect();
      if (cr.right > sr.right - 40) scroller.scrollLeft += cr.left - sr.left - 40;
    }
  }

  /* ------------------------------------------------------------------------
     5. Drawer
     ------------------------------------------------------------------------ */
  var openDrawer = function () {};
  (function () {
    if (!drawer) return;
    var panel = $('[data-llh-drawer-panel]', drawer);
    var stack = $('[data-llh-stack]', drawer);
    var rootLevel = $('[data-llh-level="root"]', drawer);
    var tilesBox = $('[data-llh-drawer-tiles]', drawer);
    var moreBox = $('[data-llh-drawer-more]', drawer);
    var openers = $$('[data-llh-drawer-open]');
    var levels = [rootLevel];
    var opener = null;
    var built = false;
    var hideTimer = null;

    var labelOf = function (tab) {
      var l = $('.llh-tab__label', tab);
      return l ? l.textContent.trim() : '';
    };

    var nut = function (cls) {
      var s = document.createElementNS(svgNS, 'svg');
      s.setAttribute('viewBox', '-50 -50 100 100');
      s.setAttribute('class', cls || 'llh-tile__nut');
      s.setAttribute('aria-hidden', 'true');
      var pg = document.createElementNS(svgNS, 'polygon');
      pg.setAttribute('points', '44,0 22,38.1 -22,38.1 -44,0 -22,-38.1 22,-38.1');
      var c = document.createElementNS(svgNS, 'circle');
      c.setAttribute('r', '16');
      s.appendChild(pg);
      s.appendChild(c);
      return s;
    };

    var build = function () {
      if (built) return;
      built = true;
      var more = [];
      tabs.forEach(function (tab) {
        var link = $('.llh-tab__link', tab);
        var p = panelFor(tab);
        var name = labelOf(tab);
        var color = colorOf(tab);
        var kind = (tab.className.match(/llh-tab--(\w+)/) || [])[1] || 'other';
        if (kind === 'game') {
          var t = p ? el('button', 'llh-tile') : el('a', 'llh-tile');
          if (p) { t.type = 'button'; t.setAttribute('data-llh-open-level', tab.getAttribute('data-llh-tab')); }
          else t.href = link.href;
          t.style.setProperty('--g', color);
          var art = p && $('img[data-llh-art]', p);
          if (art) {
            var im = document.createElement('img');
            im.className = 'llh-tile__art';
            im.alt = '';
            im.decoding = 'async';
            /* a card wall (in several sizes) shows in full across the top of the tile; the
               older collection pictures stay a faint backdrop */
            if (art.getAttribute('srcset')) {
              im.srcset = art.getAttribute('srcset');
              im.sizes = '50vw';
              t.classList.add('has-wall');
            }
            im.src = art.getAttribute('src').replace(/([?&])height=\d+/, '$1height=200');
            im.style.objectPosition = art.style.objectPosition;
            t.appendChild(im);
            t.classList.add('has-art');
          }
          t.appendChild(nut());
          t.appendChild(el('span', 'llh-tile__name', name));
          var sub = el('span', 'llh-tile__sub');
          var count = p && p.getAttribute('data-llh-count');
          sub.appendChild(el('span', null, count ? count + ' items' : 'Shop now'));
          sub.appendChild(icon(ICON.chev));
          t.appendChild(sub);
          var li = el('li');
          li.appendChild(t);
          tilesBox.appendChild(li);
        } else {
          more.push({ tab: tab, link: link, panel: p, name: name, color: color, kind: kind });
        }
      });
      if (moreBox) {
        more.reverse().forEach(function (m) {
          var row = m.panel ? el('button', 'llh-row') : el('a', 'llh-row');
          if (m.panel) { row.type = 'button'; row.setAttribute('data-llh-open-level', m.tab.getAttribute('data-llh-tab')); }
          else row.href = m.link.href;
          row.style.setProperty('--g', m.color);
          var label = el('span', null, m.name);
          if (m.kind === 'sale') label.style.color = 'var(--llh-ember-2)';
          row.appendChild(label);
          row.appendChild(icon(ICON.chev, 'llh-chev'));
          var li = el('li');
          li.appendChild(row);
          moreBox.insertBefore(li, moreBox.firstChild);
        });
      }
    };

    var rowLink = function (href, text, extra) {
      var a = el('a', 'llh-row' + (extra ? ' ' + extra : ''));
      a.href = href;
      a.appendChild(el('span', null, text));
      a.appendChild(icon(ICON.chev, 'llh-chev'));
      return a;
    };

    var makeLevel = function (title, color, backText, mark) {
      var sec = el('section', 'llh-level');
      sec.style.setProperty('--g', color);
      var head = el('div', 'llh-level__head');
      var back = el('button', 'llh-level__back');
      back.type = 'button';
      back.setAttribute('data-llh-back', '');
      back.appendChild(icon(ICON.back));
      back.appendChild(el('span', null, backText));
      head.appendChild(back);
      var h = el('p', 'llh-level__title');
      if (mark) h.appendChild(mark.cloneNode(true));
      h.appendChild(document.createTextNode(title));
      head.appendChild(h);
      sec.appendChild(head);
      var scroll = el('div', 'llh-level__scroll');
      sec.appendChild(scroll);
      return { sec: sec, scroll: scroll };
    };

    var gameLevel = function (tab) {
      var p = panelFor(tab);
      var name = labelOf(tab);
      var lv = makeLevel(name, colorOf(tab), 'Menu', $('.llh-tab__link .llh-mark', tab));
      var all = el('a', 'llh-row llh-row--all');
      all.href = p.getAttribute('data-llh-url');
      var txt = el('span');
      txt.appendChild(document.createTextNode('Shop all ' + name));
      var count = p.getAttribute('data-llh-count');
      if (count) txt.appendChild(el('small', null, count + ' items'));
      all.appendChild(txt);
      all.appendChild(icon(ICON.chev, 'llh-chev'));
      lv.scroll.appendChild(all);

      var list = el('ul', 'llh-rows');
      var eras = $$('[data-llh-era]', p);
      if (eras.length) {
        eras.forEach(function (era) {
          var n = era.getAttribute('data-llh-era');
          var group = $('[data-llh-sets="' + n + '"]', p);
          var sets = group ? $$('.llh-set', group) : [];
          var eraName = ($('.llh-era__name', era) || era).textContent.trim();
          var li = el('li');
          if (sets.length) {
            var b = el('button', 'llh-row');
            b.type = 'button';
            b.appendChild(el('span', null, eraName));
            b.appendChild(el('span', 'llh-row__n', String(sets.length)));
            b.appendChild(icon(ICON.chev, 'llh-chev'));
            b.addEventListener('click', function () { push(setsLevel(name, colorOf(tab), eraName, era.href, sets), b); });
            li.appendChild(b);
          } else {
            li.appendChild(rowLink(era.href, eraName));
          }
          list.appendChild(li);
        });
      } else {
        $$('.llh-grid__item', p).forEach(function (a) {
          var li = el('li');
          var nm = $('.llh-grid__name', a);
          li.appendChild(rowLink(a.href, (nm || a).textContent.trim()));
          list.appendChild(li);
        });
      }
      lv.scroll.appendChild(list);
      return lv.sec;
    };

    var setsLevel = function (game, color, eraName, eraUrl, sets) {
      var lv = makeLevel(eraName, color, game);
      var all = el('a', 'llh-row llh-row--all');
      all.href = eraUrl;
      var txt = el('span');
      txt.appendChild(document.createTextNode('All ' + eraName));
      txt.appendChild(el('small', null, sets.length + (sets.length === 1 ? ' set' : ' sets')));
      all.appendChild(txt);
      all.appendChild(icon(ICON.chev, 'llh-chev'));
      lv.scroll.appendChild(all);
      var list = el('ul', 'llh-rows llh-rows--sets');
      sets.forEach(function (s) {
        var li = el('li');
        li.appendChild(rowLink(s.href, s.textContent.trim()));
        list.appendChild(li);
      });
      lv.scroll.appendChild(list);
      return lv.sec;
    };

    var push = function (sec, from, instant) {
      var top = levels[levels.length - 1];
      sec.__from = from || null;
      stack.appendChild(sec);
      levels.push(sec);
      if (instant) drawer.classList.add('no-anim');
      void sec.offsetWidth;
      top.classList.remove('is-active');
      top.classList.add('is-behind');
      sec.classList.add('is-active');
      if (instant) { void sec.offsetWidth; drawer.classList.remove('no-anim'); }
      var back = $('[data-llh-back]', sec);
      if (back && !instant) setTimeout(function () { back.focus({ preventScroll: true }); }, 60);
    };

    var pop = function () {
      if (levels.length < 2) return;
      var top = levels.pop();
      var prev = levels[levels.length - 1];
      top.classList.remove('is-active');
      prev.classList.remove('is-behind');
      prev.classList.add('is-active');
      var from = top.__from;
      setTimeout(function () { if (top.parentNode) top.parentNode.removeChild(top); }, 450);
      if (from && from.focus) from.focus({ preventScroll: true });
    };

    var resetStack = function () {
      while (levels.length > 1) {
        var l = levels.pop();
        if (l.parentNode) l.parentNode.removeChild(l);
      }
      rootLevel.classList.remove('is-behind');
      rootLevel.classList.add('is-active');
      var sc = $('.llh-level__scroll', rootLevel);
      if (sc) sc.scrollTop = 0;
    };

    openDrawer = function (from, tab) {
      build();
      clearTimeout(hideTimer);
      opener = from || document.activeElement;
      if (root.__llhSearchClose) root.__llhSearchClose();
      closePanels();
      state.drawerOpen = true;
      resetStack();
      drawer.hidden = false;
      if (tab && panelFor(tab)) push(gameLevel(tab), null, true);
      void drawer.offsetWidth;
      drawer.classList.add('is-open');
      document.documentElement.classList.add('llh-lock');
      openers.forEach(function (o) { o.setAttribute('aria-expanded', 'true'); });
      setTimeout(function () {
        var first = tab ? $('[data-llh-back]', levels[levels.length - 1]) : $('.llh-drawer__x', drawer);
        if (first) first.focus({ preventScroll: true });
      }, 80);
    };

    var closeDrawer = function () {
      if (!state.drawerOpen) return;
      state.drawerOpen = false;
      drawer.classList.remove('is-open');
      panel.style.transform = '';
      document.documentElement.classList.remove('llh-lock');
      openers.forEach(function (o) { o.setAttribute('aria-expanded', 'false'); });
      hideTimer = setTimeout(function () { drawer.hidden = true; resetStack(); }, 440);
      if (opener && opener.focus) opener.focus({ preventScroll: true });
    };

    openers.forEach(function (o) {
      o.addEventListener('click', function (e) { e.preventDefault(); openDrawer(o); });
    });
    drawer.addEventListener('click', function (e) {
      var t = e.target;
      if (!t.closest) return;
      if (t.closest('[data-llh-drawer-close]')) { closeDrawer(); return; }
      if (t.closest('[data-llh-back]')) { pop(); return; }
      var lv = t.closest('[data-llh-open-level]');
      if (lv) {
        var tab = $('[data-llh-tab="' + lv.getAttribute('data-llh-open-level') + '"]', rail);
        if (tab) push(gameLevel(tab), lv);
      }
    });
    on(document, 'keydown', function (e) {
      if (!state.drawerOpen) return;
      if (e.key === 'Escape') { e.preventDefault(); closeDrawer(); return; }
      if (e.key !== 'Tab') return;
      var f = $$('a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])', panel).filter(function (n) {
        return n.offsetParent !== null && getComputedStyle(n).visibility !== 'hidden';
      });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    onChange(mqDesk, function () { if (mqDesk.matches) closeDrawer(); });

    /* Swipe the drawer closed. */
    var sx = 0, sy = 0, dx = 0, dragging = false, decided = false;
    panel.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) return;
      sx = e.touches[0].clientX; sy = e.touches[0].clientY; dx = 0; dragging = false; decided = false;
    }, { passive: true });
    panel.addEventListener('touchmove', function (e) {
      var x = e.touches[0].clientX - sx;
      var y = e.touches[0].clientY - sy;
      if (!decided) {
        if (Math.abs(x) < 8 && Math.abs(y) < 8) return;
        decided = true;
        dragging = x < 0 && Math.abs(x) > Math.abs(y) * 1.4;
        if (dragging) panel.style.transition = 'none';
      }
      if (!dragging) return;
      dx = Math.min(0, x);
      panel.style.transform = 'translate3d(' + dx + 'px,0,0)';
    }, { passive: true });
    panel.addEventListener('touchend', function () {
      if (!dragging) return;
      panel.style.transition = '';
      if (dx < -70) closeDrawer();
      else panel.style.transform = '';
      dragging = false;
    });
  }());

  /* ------------------------------------------------------------------------
     6. Counts
     ------------------------------------------------------------------------ */
  (function () {
    var cart = $('[data-llh-cart]', root);
    var badge = $('[data-llh-cart-count]', root);
    if (!cart || !badge) return;
    var shown = parseInt(badge.textContent, 10) || 0;

    var paint = function (n) {
      n = parseInt(n, 10);
      if (isNaN(n) || n < 0) return;
      var up = n > shown;
      shown = n;
      badge.textContent = n > 99 ? '99+' : String(n);
      badge.setAttribute('data-header-cart-count', n ? String(n) : '');
      badge.hidden = n === 0;
      cart.setAttribute('data-cart-has-items', n > 0 ? 'true' : 'false');
      if (up) {
        badge.classList.remove('is-bump');
        void badge.offsetWidth;
        badge.classList.add('is-bump');
      }
    };

    var inFlight = false;
    var again = false;
    var refresh = function () {
      if (inFlight) { again = true; return; }
      inFlight = true;
      fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart.js', {
        credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store'
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (c) { if (c) paint(c.item_count); })
        .catch(function () {})
        .then(function () { inFlight = false; if (again) { again = false; refresh(); } });
    };
    var soon = null;
    var refreshSoon = function () { clearTimeout(soon); soon = setTimeout(refresh, 120); };

    ['cart:updated', 'cart:refresh', 'cart:build', 'cartcount:update', 'll:cart:updated',
     'll:cart:change', 'll:cart:applied', 'theme:cart:change', 'ajaxProduct:added'].forEach(function (name) {
      on(document, name, function (e) {
        var c = e && e.detail && (e.detail.cart || e.detail);
        if (c && typeof c.item_count === 'number') paint(c.item_count);
        else refreshSoon();
      });
    });

    /* Anything that writes to the cart, whoever wrapped fetch, shows up as a
       resource timing entry. Watching those needs no patching of fetch. */
    if (window.PerformanceObserver) {
      try {
        var re = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
        var po = new PerformanceObserver(function (list) {
          list.getEntries().forEach(function (en) { if (re.test(en.name)) refreshSoon(); });
        });
        po.observe({ type: 'resource', buffered: false });
        cleanups.push(function () { po.disconnect(); });
      } catch (e) {}
    }
    var tb = document.querySelector('[data-llmt-cart-count]');
    if (tb && window.MutationObserver) {
      var mo = new MutationObserver(function () {
        var n = tb.hidden ? 0 : parseInt(String(tb.textContent || '').replace(/[^0-9]/g, ''), 10);
        if (!isNaN(n) && n !== shown) paint(n);
      });
      mo.observe(tb, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
      cleanups.push(function () { mo.disconnect(); });
    }
    on(window, 'pageshow', function (e) { if (e.persisted) refresh(); });
    var lastVis = Date.now();
    on(document, 'visibilitychange', function () {
      if (document.visibilityState === 'visible' && Date.now() - lastVis > 20000) refresh();
      lastVis = Date.now();
    });
  }());

  (function () {
    var box = $('[data-ll-header-watch]', root);
    if (!box) return;
    var num = $('[data-ll-header-watch-count]', box);
    var sr = $('[data-ll-header-watch-sr]', box);
    var KEY = 'll_watchlist';
    var LEGACY = 'll_preorder_watch';
    var shown = -1;

    var read = function () {
      try {
        var raw = store.get('localStorage', KEY);
        if (raw == null) raw = store.get('localStorage', LEGACY);
        var list = raw ? JSON.parse(raw) : [];
        if (Object.prototype.toString.call(list) !== '[object Array]') return 0;
        var seen = {}, n = 0;
        for (var i = 0; i < list.length; i++) {
          var h = String(list[i] || '').trim();
          if (!h || seen[h]) continue;
          seen[h] = 1;
          n++;
        }
        return n;
      } catch (e) { return 0; }
    };

    var paint = function () {
      var n = read();
      box.setAttribute('data-count', String(n));
      if (num) {
        num.textContent = n > 99 ? '99+' : String(n);
        num.hidden = n < 1;
      }
      if (sr) sr.textContent = n === 1 ? 'Your watchlist, 1 item' : n ? 'Your watchlist, ' + n + ' items' : 'Your watchlist';
      if (shown > -1 && n > shown) {
        box.classList.remove('is-ring');
        void box.offsetWidth;
        box.classList.add('is-ring');
        if (num) { num.classList.remove('is-bump'); void num.offsetWidth; num.classList.add('is-bump'); }
      }
      shown = n;
    };

    paint();
    on(document, 'll:watchlist:change', paint);
    on(window, 'storage', function (e) {
      if (!e || !e.key || e.key === KEY || e.key === LEGACY) paint();
    });
    /* The account sync can land a moment after load. */
    setTimeout(paint, 1200);
  }());
}

  boot();
  if (window.__llhHooked) return;
  window.__llhHooked = true;

  /* Theme editor: re-render of the header section. */
  document.addEventListener('shopify:section:unload', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('[data-llh]') && window.__llhTeardown) window.__llhTeardown();
  });
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('[data-llh]')) boot();
  });
}());
